import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import { toast } from '../../utils/toast';
import { API_MLINKONE_COMPANY_PROFILE } from '../../constants/apiConstants';

// ============================================================================
// Types — mirror SmartMatrix.Domain MLinkOne CompanyProfile_* payloads.
// Spec: mlinkone-supplier-company-profile. The profile is the supplier party's
// company info (Party subset) + its `ship_from` PartyAddress, projected flat.
// ============================================================================

export interface CompanyProfile {
  partyId: number;
  /** PartyCode — the supplier's stable identity; read-only (not editable here). */
  shortCode: string;
  companyName: string;
  contactPerson: string | null;
  tel: string | null;
  fax: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  addressLine4: string | null;
  city: string | null;
  provinceState: string | null;
  postalCode: string | null;
  /** Display NAME of the country (e.g. "Hong Kong") — the primary stored value. */
  country: string | null;
  /** ISO alpha-2 code; set when `country` resolves to a syscountry_codes row. */
  countryCode: string | null;
  /** Whether the caller may edit + save this profile (false → view-only). */
  canManage: boolean;
  /** Labelling Information tab — courier + Delivery Address (null until first save). */
  labelling: CompanyProfileLabelling | null;
}

/** Labelling Information — courier (PartyCarrierAccount) + Delivery Address (shipping). */
export interface CompanyProfileLabelling {
  deliveryType: string | null;
  courierName: string | null;
  accountNo: string | null;
  contactNo: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  addressLine4: string | null;
  city: string | null;
  provinceState: string | null;
  postalCode: string | null;
  country: string | null;
  countryCode: string | null;
}

/** PUT body for the Labelling tab. */
export interface CompanyProfileLabellingUpdateRequest extends CompanyProfileLabelling {
  tenantId: number;
  supplierPartyId: number;
}

/** PUT body — the editable profile subset (ShortCode/Classification are NOT writable). */
export interface CompanyProfileUpdateRequest {
  tenantId: number;
  supplierPartyId: number;
  companyName: string;
  contactPerson?: string | null;
  tel?: string | null;
  fax?: string | null;
  email?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressLine3?: string | null;
  addressLine4?: string | null;
  city?: string | null;
  provinceState?: string | null;
  postalCode?: string | null;
  country?: string | null;
  countryCode?: string | null;
}

// Backend Result<T> envelope (shared shape across the app).
interface ResultEnvelope<T> {
  succeeded: boolean;
  statusCode?: number;
  messages?: string[];
  data?: T;
}

// GET response — { profile, message }.
interface CompanyProfileGetResponse {
  profile: CompanyProfile | null;
  message?: string | null;
}

// PUT response — { supplierPartyId, message }.
interface CompanyProfileUpdateResponse {
  supplierPartyId: number;
  message?: string | null;
}

// ============================================================================
// Service
// ============================================================================

export const companyProfileService = {
  /** Load the supplier party's company profile (Party + ship_from address). */
  async getCompanyProfile(tenantId: number, supplierPartyId: number): Promise<CompanyProfile> {
    try {
      const response = await apiClient.get<ResultEnvelope<CompanyProfileGetResponse>>(
        API_MLINKONE_COMPANY_PROFILE,
        { params: { tenantId, supplierPartyId } },
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to load company profile');
      }
      const profile = response.data.data?.profile;
      if (!profile) throw new Error('Company profile not found');
      return profile;
    } catch (error) {
      logger.error('Failed to load company profile', error);
      throw error;
    }
  },

  /** Upsert the supplier party's company profile. Throws (and toasts) on failure. */
  async updateCompanyProfile(request: CompanyProfileUpdateRequest): Promise<CompanyProfileUpdateResponse> {
    try {
      const response = await apiClient.put<ResultEnvelope<CompanyProfileUpdateResponse>>(
        API_MLINKONE_COMPANY_PROFILE,
        request,
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to save company profile');
      }
      const data = response.data.data;
      if (!data) throw new Error('No response returned after save');
      return data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to save company profile: ${msg}`);
      throw error;
    }
  },

  /** Upsert the Labelling Information (courier + delivery address). Throws (and toasts) on failure. */
  async updateCompanyProfileLabelling(request: CompanyProfileLabellingUpdateRequest): Promise<CompanyProfileUpdateResponse> {
    try {
      const response = await apiClient.put<ResultEnvelope<CompanyProfileUpdateResponse>>(
        `${API_MLINKONE_COMPANY_PROFILE}/labelling`,
        request,
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to save labelling information');
      }
      const data = response.data.data;
      if (!data) throw new Error('No response returned after save');
      return data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to save labelling information: ${msg}`);
      throw error;
    }
  },
};
